import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { LIVE_NAMESPACE } from '@brimostech/shared';
import { session } from './api-client';

/**
 * Subscribes to the live feed of the active condominium. Reconnects with the current access
 * token (the server validates the session on every connection).
 */
export function useLiveEvent<T>(
  event: string,
  onEvent: (payload: T) => void,
  enabled = true,
): void {
  const handler = useRef(onEvent);
  handler.current = onEvent;
  useEffect(() => {
    if (!enabled) return;
    let socket: Socket | null = null;
    const connect = () => {
      socket?.disconnect();
      socket = io(LIVE_NAMESPACE, {
        path: '/socket.io',
        auth: { token: session.getAccessToken() },
        transports: ['websocket', 'polling'],
      });
      socket.on(event, (payload: T) => handler.current(payload));
    };
    connect();
    const unsubscribe = session.subscribe((user) => {
      if (user) connect();
    });
    return () => {
      unsubscribe();
      socket?.disconnect();
    };
  }, [event, enabled]);
}

/**
 * Declares this browser as a front desk screen: intercom calls nobody answers fall back to it.
 * Keeps one socket open for the page and re-declares the presence after every reconnection.
 */
export function useFrontDeskPresence(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    let socket: Socket | null = null;
    const connect = () => {
      socket?.disconnect();
      socket = io(LIVE_NAMESPACE, {
        path: '/socket.io',
        auth: { token: session.getAccessToken() },
        transports: ['websocket', 'polling'],
      });
      socket.on('ready', () => socket?.emit('frontdesk:presence', { online: true }));
    };
    connect();
    const unsubscribe = session.subscribe((user) => {
      if (user) connect();
    });
    return () => {
      unsubscribe();
      socket?.emit('frontdesk:presence', { online: false });
      socket?.disconnect();
    };
  }, [enabled]);
}
